
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import type {
  BatchConfiguration,
  CampaignCharacter,
  CampaignSummary,
  CampaignUser,
  CampaignUserFilterKey,
  CampaignUserSortOption,
  EmailAutomationFeature,
  SendTimeOption,
} from "../types";
import { DEFAULT_SEND_TIME_VALUE } from "../types";
import { SectionCard } from "./SectionLayout";
import {
  fireManualCampaignTriggers,
  type ManualTriggerRequestEntry,
  type ManualTriggerType,
} from "../../../../api/campaigns";
import { AdvancedFilterModal } from "./AdvancedFilterModal";
import { CampaignFilterModal } from "./CampaignFilterModal";

interface BatchTestingSectionProps {
  users: CampaignUser[];
  batchConfigurations: BatchConfiguration[];
  sendTimeOptions: SendTimeOption[];
  isLoadingUsers?: boolean;
  isUsingRemoteUsers?: boolean;
  sortOption: CampaignUserSortOption;
  onChangeSort: (option: CampaignUserSortOption) => void;
  filterToggles: Record<CampaignUserFilterKey, boolean>;
  onToggleFilter: (key: CampaignUserFilterKey) => void;
  currentCampaignName?: string;
  campaignOptions?: CampaignSummary[];
  selectedCampaignId?: string;
  onSelectCampaign?: (campaignId: string) => void;
  minTokensFilter?: number | null;
  maxTokensFilter?: number | null;
  fromDateFilter?: string | null;
  toDateFilter?: string | null;
  maxConversationCountFilter?: number | null;
  minUniqueCharactersFilter?: number | null;
  requiredCharacterFilters?: string[];
  availableCharacters?: CampaignCharacter[];
  onApplyAdvancedFilters?: (filters: {
    minTokens: number | null;
    maxTokens: number | null;
    fromDate: string | null;
    toDate: string | null;
    maxConversationCount: number | null;
    minUniqueCharacters: number | null;
    requiredCharacterIds: string[];
  }) => void;
}

const formatDate = (value?: string) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDateOnly = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTokens = (value: number) =>
  `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} tokens`;

const FEATURE_OPTIONS: Array<{
  id: EmailAutomationFeature;
  label: string;
  description: string;
}> = [
  {
    id: "reengagement-email-sequence",
    label: "Reengagement Email Sequence",
    description: "Run the staged follow-up sequence for inactive users.",
  },
  {
    id: "no-chat-email",
    label: "No-Chat Introductions",
    description: "Send the first-touch email for users who never chatted.",
  },
  {
    id: "premium-triggers",
    label: "Reach-out Rules",
    description: "Trigger reach-outs based on character thresholds.",
  },
];

const FEATURE_TRIGGER_MAP: Record<
  EmailAutomationFeature,
  ManualTriggerType | null
> = {
  "reengagement-email-sequence": "reengagement",
  "no-chat-email": "no_chat_intro",
  "premium-triggers": "reachout",
};

const SORT_OPTIONS: Array<{
  value: CampaignUserSortOption;
  label: string;
}> = [
  { value: "latest", label: "Latest Activity" },
  { value: "conversations", label: "Most Conversations" },
];

const FILTER_TOGGLES: Array<{
  key: CampaignUserFilterKey;
  label: string;
}> = [
  { key: "paidUsersOnly", label: "Paid Users" },
  { key: "ranOutOfTokensOnly", label: "Ever Ran Out of Tokens" },
];

export const BatchTestingSection: React.FC<BatchTestingSectionProps> = ({
  users,
  batchConfigurations,
  sendTimeOptions,
  isLoadingUsers = false,
  isUsingRemoteUsers = false,
  sortOption,
  onChangeSort,
  filterToggles,
  onToggleFilter,
  currentCampaignName,
  campaignOptions = [],
  selectedCampaignId = "",
  onSelectCampaign,
  minTokensFilter = null,
  maxTokensFilter = null,
  fromDateFilter = null,
  toDateFilter = null,
  maxConversationCountFilter = null,
  minUniqueCharactersFilter = null,
  requiredCharacterFilters = [],
  availableCharacters = [],
  onApplyAdvancedFilters,
}) => {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [selectedConfigurationId, setSelectedConfigurationId] = useState(
    batchConfigurations[0]?.id ?? ""
  );
  const [featureSelections, setFeatureSelections] = useState<
    Record<EmailAutomationFeature, boolean>
  >(() =>
    FEATURE_OPTIONS.reduce(
      (accumulator, option) => ({ ...accumulator, [option.id]: true }),
      {} as Record<EmailAutomationFeature, boolean>
    )
  );
  const [campaignFilters, setCampaignFilters] = useState<Record<string, 'include' | 'exclude'>>({});
  const [isCampaignFilterModalOpen, setIsCampaignFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isTriggeringBatch, setIsTriggeringBatch] = useState(false);
  const [isAdvancedFilterModalOpen, setIsAdvancedFilterModalOpen] =
    useState(false);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    sortOption,
    filterToggles.paidUsersOnly,
    filterToggles.ranOutOfTokensOnly,
    campaignFilters,
    minTokensFilter,
    maxTokensFilter,
    fromDateFilter,
    toDateFilter,
  ]);

  useEffect(() => {
    setCampaignFilters((current) => {
      const availableIds = new Set(campaignOptions.map((o) => o.id));
      const next: Record<string, 'include' | 'exclude'> = {};

      Object.keys(current).forEach((id) => {
        if (availableIds.has(id)) {
          next[id] = current[id];
        }
      });

      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [campaignOptions]);

  useEffect(() => {
    if (!selectedConfigurationId && batchConfigurations[0]) {
      setSelectedConfigurationId(batchConfigurations[0].id);
      return;
    }
    if (
      selectedConfigurationId &&
      !batchConfigurations.some(
        (configuration) => configuration.id === selectedConfigurationId
      )
    ) {
      setSelectedConfigurationId(batchConfigurations[0]?.id ?? "");
    }
  }, [batchConfigurations, selectedConfigurationId]);

  const campaignNameById = useMemo(() => {
    return campaignOptions.reduce<Record<string, string>>(
      (accumulator, option) => {
        accumulator[option.id] = option.name;
        return accumulator;
      },
      {}
    );
  }, [campaignOptions]);

  const characterNameById = useMemo(() => {
    return availableCharacters.reduce<Record<string, string>>(
      (accumulator, character) => {
        accumulator[character.id] = character.name;
        return accumulator;
      },
      {}
    );
  }, [availableCharacters]);

const isPaidUser = (user: CampaignUser) => {
  const badge = (user.membershipBadge || "").trim().toLowerCase();
  if (badge && badge !== "subscriber") {
    return true;
  }
  return false;
};

const hasRanOutOfTokens = (user: CampaignUser) => {
  return Boolean(user.everRanOutOfTokens);
};

  const campaignFilterLabel = useMemo(() => {
    const selectedCampaigns = Object.keys(campaignFilters);
    if (selectedCampaigns.length === 0) return "All campaigns";

    const parts = selectedCampaigns.map((id) => {
      const name = campaignNameById[id] || id;
      const mode = campaignFilters[id];
      const prefix = mode === 'include' ? '' : 'NOT ';
      return `${prefix}${name}`;
    });

    if (parts.length === 1) {
      return parts[0];
    }

    return `${parts.length} conditions`;
  }, [campaignFilters, campaignNameById]);

  const isCampaignFilterActive = Object.keys(campaignFilters).length > 0;

  const visibleUsers = useMemo(() => {
    const normalizedMaxConversations =
      typeof maxConversationCountFilter === "number"
        ? maxConversationCountFilter
        : null;
    const normalizedMinUniqueCharacters =
      typeof minUniqueCharactersFilter === "number"
        ? minUniqueCharactersFilter
        : null;

    const filtered = users.filter((user) => {
      const matchesSearch =
        search.length === 0 ||
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        `${user.firstName} ${user.lastName}`
          .toLowerCase()
          .includes(search.toLowerCase());
      const matchesPaid = !filterToggles.paidUsersOnly || isPaidUser(user);
      const matchesRanOut =
        !filterToggles.ranOutOfTokensOnly || hasRanOutOfTokens(user);
      const matchesCampaign = (() => {
        const selectedCampaigns = Object.keys(campaignFilters);
        if (selectedCampaigns.length === 0) return true;

        // User must satisfy ALL conditions (AND logic)
        return selectedCampaigns.every((campaignId) => {
          const mode = campaignFilters[campaignId];
          const userReceivedCampaign =
            user.campaignId === campaignId ||
            user.campaignName === campaignNameById[campaignId] ||
            user.campaigns?.some((campaign) => campaign.campaignId === campaignId);

          // For 'include': user must have received it
          // For 'exclude': user must NOT have received it
          return mode === 'include' ? userReceivedCampaign : !userReceivedCampaign;
        });
      })();
      const totalConversationCount = user.totalConversations ?? 0;
      const meetsMaxConversations =
        normalizedMaxConversations === null ||
        totalConversationCount <= normalizedMaxConversations;
      const userCharacterIds = user.characters.map(
        (character) => character.characterId
      );
      const uniqueCharacterCount = new Set(userCharacterIds).size;
      const meetsUniqueCharacterCount =
        normalizedMinUniqueCharacters === null ||
        uniqueCharacterCount >= normalizedMinUniqueCharacters;
      const meetsCharacterSelection =
        requiredCharacterFilters.length === 0 ||
        requiredCharacterFilters.every((id) => userCharacterIds.includes(id));
      return (
        matchesSearch &&
        matchesPaid &&
        matchesRanOut &&
        matchesCampaign &&
        meetsMaxConversations &&
        meetsUniqueCharacterCount &&
        meetsCharacterSelection
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortOption === "latest") {
        const left = a.lastInteractionAt
          ? new Date(a.lastInteractionAt).getTime()
          : 0;
        const right = b.lastInteractionAt
          ? new Date(b.lastInteractionAt).getTime()
          : 0;
        return right - left;
      }
      if (sortOption === "conversations") {
        const left = a.totalConversations ?? a.characters.length;
        const right = b.totalConversations ?? b.characters.length;
        if (right === left) {
          return (b.totalTokens ?? 0) - (a.totalTokens ?? 0);
        }
        return right - left;
      }
      return 0;
    });

    return sorted;
  }, [
    users,
    search,
    filterToggles.paidUsersOnly,
    filterToggles.ranOutOfTokensOnly,
    sortOption,
    campaignFilters,
    campaignNameById,
    maxConversationCountFilter,
    minUniqueCharactersFilter,
    requiredCharacterFilters,
  ]);

  const totalUsers = visibleUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return visibleUsers.slice(startIndex, startIndex + pageSize);
  }, [visibleUsers, currentPage, pageSize]);
  const startIndex = totalUsers === 0 ? 0 : (currentPage - 1) * pageSize;
  const endIndex =
    totalUsers === 0 ? 0 : Math.min(startIndex + pageSize, totalUsers);
  const displayStart = totalUsers === 0 ? 0 : startIndex + 1;
  const displayEnd = totalUsers === 0 ? 0 : endIndex;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const toggleSelection = (userId: string) => {
    setSelectedIds((current) => ({
      ...current,
      [userId]: !current[userId],
    }));
  };

  const selectedUsers = useMemo(
    () =>
      visibleUsers.filter((user) => {
        if (selectedIds[user.id]) return true;
        return false;
      }),
    [visibleUsers, selectedIds]
  );

  const allFilteredSelected =
    totalUsers > 0 && visibleUsers.every((user) => selectedIds[user.id]);

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds({});
      return;
    }

    setSelectedIds((current) => {
      const next = { ...current };
      visibleUsers.forEach((user) => {
        next[user.id] = true;
      });
      return next;
    });
  };

  const toggleFeature = (id: EmailAutomationFeature) => {
    setFeatureSelections((current) => ({
      ...current,
      [id]: !current[id],
    }));
  };

  const selectAllFeatures = (value: boolean) => {
    setFeatureSelections(
      FEATURE_OPTIONS.reduce(
        (accumulator, option) => ({ ...accumulator, [option.id]: value }),
        {} as Record<EmailAutomationFeature, boolean>
      )
    );
  };

  const selectedFeatures = useMemo(
    () =>
      FEATURE_OPTIONS.filter((option) => featureSelections[option.id]).map(
        (option) => option.id
      ),
    [featureSelections]
  );

  const allFeaturesSelected =
    selectedFeatures.length === FEATURE_OPTIONS.length;

  const resolvedCampaignLabel =
    currentCampaignName?.trim() || "Select a campaign";

  const advancedFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (typeof minTokensFilter === "number") {
      parts.push(`≥ ${minTokensFilter.toLocaleString()} tokens`);
    }
    if (typeof maxTokensFilter === "number") {
      parts.push(`≤ ${maxTokensFilter.toLocaleString()} tokens`);
    }
    if (fromDateFilter) {
      parts.push(`from ${formatDateOnly(fromDateFilter)}`);
    }
    if (toDateFilter) {
      parts.push(`to ${formatDateOnly(toDateFilter)}`);
    }
    if (typeof maxConversationCountFilter === "number") {
      parts.push(`≤ ${maxConversationCountFilter} chats`);
    }
    if (typeof minUniqueCharactersFilter === "number") {
      parts.push(`with ≥ ${minUniqueCharactersFilter} characters`);
    }
    if (requiredCharacterFilters.length) {
      const resolvedNames = requiredCharacterFilters.map(
        (id) => characterNameById[id] ?? id
      );
      parts.push(`must include ${resolvedNames.join(", ")}`);
    }
    return parts.join(" · ");
  }, [
    minTokensFilter,
    maxTokensFilter,
    fromDateFilter,
    toDateFilter,
    maxConversationCountFilter,
    minUniqueCharactersFilter,
    requiredCharacterFilters,
    characterNameById,
  ]);

  const advancedFilterCount = useMemo(() => {
    let count = 0;
    if (typeof minTokensFilter === "number") count += 1;
    if (typeof maxTokensFilter === "number") count += 1;
    if (fromDateFilter) count += 1;
    if (toDateFilter) count += 1;
    if (typeof maxConversationCountFilter === "number") count += 1;
    if (typeof minUniqueCharactersFilter === "number") count += 1;
    if (requiredCharacterFilters.length > 0) count += 1;
    return count;
  }, [
    minTokensFilter,
    maxTokensFilter,
    fromDateFilter,
    toDateFilter,
    maxConversationCountFilter,
    minUniqueCharactersFilter,
    requiredCharacterFilters,
  ]);

  const handleLaunchBatch = async () => {
    if (!selectedUsers.length || !selectedFeatures.length) {
      return;
    }

    if (!selectedCampaignId) {
      toast.error("Select a campaign before triggering sends.");
      return;
    }

    const triggerTypes = selectedFeatures
      .map((feature) => FEATURE_TRIGGER_MAP[feature])
      .filter((trigger): trigger is ManualTriggerType => Boolean(trigger));

    if (!triggerTypes.length) {
      toast.error("Select at least one automation to trigger.");
      return;
    }

    const payload: ManualTriggerRequestEntry[] = selectedUsers.map((user) => ({
      userId: user.id,
      triggers: triggerTypes,
    }));

    try {
      setIsTriggeringBatch(true);
      const response = await fireManualCampaignTriggers(
        selectedCampaignId,
        payload
      );
      const queuedCount = response.count ?? selectedUsers.length;
      toast.success(
        `Queued manual triggers for ${queuedCount} user${
          queuedCount === 1 ? "" : "s"
        }.`
      );
      setSelectedIds({});
      selectAllFeatures(true);
    } catch (error) {
      console.error("Failed to fire manual campaign triggers", error);
      toast.error("Failed to trigger emails. Please try again.");
    } finally {
      setIsTriggeringBatch(false);
    }
  };

  const handleAdvancedFiltersReset = () => {
    if (onApplyAdvancedFilters) {
      onApplyAdvancedFilters({
        minTokens: null,
        maxTokens: null,
        fromDate: null,
        toDate: null,
        maxConversationCount: null,
        minUniqueCharacters: null,
        requiredCharacterIds: [],
      });
    }
  };

  return (
    <div className="space-y-6">
      <SectionCard
        title="User Selection"
        description="Filter, batch, and experiment on targeted user cohorts."
        actions={
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>Suggested batch size: 50–100</span>
            {isLoadingUsers ? <span>Loading campaign users…</span> : null}
          </div>
        }
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex w-full flex-col gap-3 md:flex-row md:items-center">
            <div className="relative w-full md:max-w-sm">
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name, email, or character…"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pl-11 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              />
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 3.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="m15.5 15.5-2.5-2.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </div>
            <div
              className="relative w-full md:max-w-xs"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Filter campaigns
              </span>
              <button
                type="button"
                onClick={() => setIsCampaignFilterModalOpen(true)}
                disabled={!campaignOptions.length}
                className={`mt-1 flex w-full items-center justify-between rounded-xl border bg-white px-4 py-2.5 text-left text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 ${
                  isCampaignFilterActive
                    ? "border-[var(--primary-color)] text-[var(--primary-color)]"
                    : "border-gray-200 text-gray-800"
                }`}
              >
                <span>{campaignFilterLabel}</span>
                <svg
                  className="h-4 w-4 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m6 8 4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2 md:items-end">
            <div className="flex items-center gap-2 overflow-x-auto whitespace-nowrap">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onChangeSort(option.value)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    sortOption === option.value
                      ? "border-[var(--primary-color)] bg-[var(--primary-color)] text-white shadow-sm"
                      : "border-gray-200 bg-gray-100 text-gray-600 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
                  }`}
                >
                  {option.label}
                </button>
              ))}
              {FILTER_TOGGLES.map((toggle) => {
                const isActive = filterToggles[toggle.key];
                return (
                  <button
                    key={toggle.key}
                    type="button"
                    onClick={() => onToggleFilter(toggle.key)}
                    className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      isActive
                        ? "border-[var(--primary-color)] bg-[var(--primary-color)] text-white shadow-sm"
                        : "border-gray-200 bg-gray-100 text-gray-600 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
                    }`}
                  >
                    {toggle.label}
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-gray-500">
              Showing {displayStart}-{displayEnd} of {totalUsers} filtered users
              (out of {users.length} total)
              {totalUsers > 0 ? (
                <span className="ml-2 text-[11px] text-gray-400">
                  Select-all checkbox applies to every filtered user (
                  {totalUsers} total).
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-gray-500">
              {advancedFilterSummary
                ? `Advanced filters active: ${advancedFilterSummary}`
                : "No advanced filters applied yet."}
            </div>
            <button
              type="button"
              onClick={() => setIsAdvancedFilterModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.3 2.3a1.3 1.3 0 0 0-2.6 0l-.2 1A5.3 5.3 0 0 0 6.3 4l-.9-.5a1.3 1.3 0 0 0-1.8.5L2.8 6a1.3 1.3 0 0 0 .5 1.8l.9.5a5.3 5.3 0 0 0 0 2.1l-.9.5a1.3 1.3 0 0 0-.5 1.8l.8 1.4a1.3 1.3 0 0 0 1.8.5l.9-.5a5.3 5.3 0 0 0 2.2.7l.2 1a1.3 1.3 0 0 0 2.6 0l.2-1a5.3 5.3 0 0 0 2.2-.7l.9.5a1.3 1.3 0 0 0 1.8-.5l.8-1.4a1.3 1.3 0 0 0-.5-1.8l-.9-.5a5.3 5.3 0 0 0 0-2.1l.9-.5a1.3 1.3 0 0 0 .5-1.8l-.8-1.4a1.3 1.3 0 0 0-1.8-.5l-.9.5a5.3 5.3 0 0 0-2.2-.7l-.2-1Z"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 12.2a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>Advanced filters</span>
              {advancedFilterCount > 0 ? (
                <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--primary-color)] px-2 text-[11px] font-semibold text-white">
                  {advancedFilterCount}
                </span>
              ) : null}
            </button>
          </div>
        </div>

        <AdvancedFilterModal
            isOpen={isAdvancedFilterModalOpen}
            onClose={() => setIsAdvancedFilterModalOpen(false)}
            onApply={onApplyAdvancedFilters || (() => {})}
            onReset={handleAdvancedFiltersReset}
            initialValues={{
                minTokens: minTokensFilter,
                maxTokens: maxTokensFilter,
                fromDate: fromDateFilter,
                toDate: toDateFilter,
                maxConversationCount: maxConversationCountFilter,
                minUniqueCharacters: minUniqueCharactersFilter,
                requiredCharacterIds: requiredCharacterFilters,
            }}
            availableCharacters={availableCharacters}
            isLoading={isLoadingUsers}
        />

        <CampaignFilterModal
          isOpen={isCampaignFilterModalOpen}
          onClose={() => setIsCampaignFilterModalOpen(false)}
          onApply={(filters) => {
            setCampaignFilters(filters);
            setIsCampaignFilterModalOpen(false);
          }}
          campaignOptions={campaignOptions}
          initialFilters={campaignFilters}
          users={users}
          campaignNameById={campaignNameById}
        />

        <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                  />
                </th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Last Active</th>
                <th className="px-4 py-3">Characters & Tokens</th>
                <th className="px-4 py-3">Campaign</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white text-sm text-gray-600">
              {paginatedUsers.map((user) => {
                const userMessagesUrl = `/admin/messages/${user.id}`;
                return (
                  <tr
                    key={user.id}
                    className={
                      selectedIds[user.id] ? "bg-[var(--primary-color)]/5" : ""
                    }
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={!!selectedIds[user.id]}
                        onChange={() => toggleSelection(user.id)}
                        className="h-4 w-4 rounded border-gray-300 text-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={userMessagesUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex flex-col text-[var(--primary-color)]"
                      >
                        <span className="font-semibold group-hover:underline">
                          {user.firstName} {user.lastName}
                        </span>
                        <span className="text-xs text-[var(--primary-color)]/80 group-hover:underline">
                          {user.email}
                        </span>
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      {user.lastInteractionAt ? (
                        <div className="text-sm font-semibold text-gray-800">
                          {formatDate(user.lastInteractionAt)}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">
                          No recent activity
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {user.characters.length ? (
                          user.characters.map((character) => (
                            <span
                              key={character.characterId}
                              className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600"
                            >
                              <span className="font-semibold text-gray-900">
                                {character.name}
                              </span>
                              <span>{formatTokens(character.tokensUsed)}</span>
                            </span>
                          ))
                        ) : (
                          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500">
                            No chats yet
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {user.campaigns?.length ? (
                        <div className="flex flex-wrap gap-2">
                          {user.campaigns.map((campaign) => (
                            <div
                              key={`${user.id}-${campaign.campaignId}`}
                              className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700"
                            >
                              <div className="font-semibold text-gray-900">
                                {campaign.campaignName}
                              </div>
                              {campaign.lastSentAt ? (
                                <div className="text-[11px] text-gray-500">
                                  Last sent{" "}
                                  {formatDateOnly(campaign.lastSentAt)}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500">
                          No campaign history
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-xs text-gray-500">
            Showing {displayStart}-{displayEnd} of {totalUsers} users
          </div>
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Rows per page:</span>
              <select
                value={pageSize}
                onChange={(event) => {
                  const nextSize = Number(event.target.value);
                  setPageSize(nextSize);
                  setPage(1);
                }}
                className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                {[10, 25, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage <= 1}
                className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] disabled:cursor-not-allowed disabled:border-gray-100 disabled:text-gray-300"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Page {totalUsers === 0 ? 0 : currentPage} of{" "}
                {totalUsers === 0 ? 0 : totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage >= totalPages || totalUsers === 0}
                className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] disabled:cursor-not-allowed disabled:border-gray-100 disabled:text-gray-300"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[2fr,3fr] gap-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-base font-semibold text-gray-900">
                  Batch Actions
                </div>
                <div className="text-xs text-gray-500">
                  Choose which configuration and send time to experiment with.
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 text-right">
                <span className="text-xs font-semibold text-gray-500">
                  {selectedUsers.length} users selected
                </span>
                <span className="inline-flex items-center rounded-full border border-gray-200 px-3 py-1 text-[11px] font-semibold text-gray-600">
                  Campaign: {resolvedCampaignLabel}
                </span>
              </div>
            </div>
            <div className="mt-4 space-y-4">
              <label className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Select campaign
                </span>
                <select
                  value={selectedCampaignId}
                  onChange={(event) => onSelectCampaign?.(event.target.value)}
                  disabled={!campaignOptions.length}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] disabled:bg-gray-50 disabled:text-gray-400"
                >
                  {campaignOptions.length === 0 ? (
                    <option value="">No campaigns available</option>
                  ) : (
                    campaignOptions.map((campaign) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                        {campaign.status === "active" ? " (Live)" : ""}
                      </option>
                    ))
                  )}
                </select>
                <span className="text-xs text-gray-500">
                  Currently editing: {resolvedCampaignLabel}
                </span>
              </label>

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                      Included automations
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Choose which email features this batch will activate.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => selectAllFeatures(!allFeaturesSelected)}
                    className="text-xs font-semibold text-[var(--primary-color)] hover:underline"
                  >
                    {allFeaturesSelected ? "Clear" : "Select all"}
                  </button>
                </div>
                <div className="mt-3 space-y-2">
                  {FEATURE_OPTIONS.map((feature) => {
                    const checked = featureSelections[feature.id];
                    return (
                      <label
                        key={feature.id}
                        className={`flex items-start gap-3 rounded-xl border px-3 py-2 transition ${
                          checked
                            ? "border-[var(--primary-color)] bg-white"
                            : "border-gray-200 bg-white hover:border-[var(--primary-color)]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleFeature(feature.id)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-[var(--primary-color)] focus:ring-[var(--primary-color)]"
                        />
                        <span className="text-sm text-gray-700">
                          <span className="block font-semibold text-gray-900">
                            {feature.label}
                          </span>
                          <span className="text-xs text-gray-500">
                            {feature.description}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <button
                type="button"
                onClick={handleLaunchBatch}
                disabled={
                  !selectedUsers.length ||
                  !selectedFeatures.length ||
                  isTriggeringBatch
                }
                className="w-full rounded-xl bg-[var(--primary-color)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {isTriggeringBatch ? "Sending..." : "Send to Selected Users"}
              </button>
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  );
};
